/**
 * Sprint 6b — geofences (ADR 0015): forbidden zones, work areas and dwell limits.
 *
 * The database is the single authority that raises alarms; this module implements the SAME rules so
 * the console can validate and preview zones, and so the SQL implementation can be tested for parity
 * against the shared vectors in packages/core/test/fixtures/geofence-vectors.json.
 *
 * Pure TypeScript, no I/O and no runtime APIs. Distances are metres on a local equirectangular
 * projection around the shape's reference point (site scale; PostGIS is not needed):
 *   x = (lon − lon0) · cos(lat0) · R · π/180,  y = (lat − lat0) · R · π/180,  R = 6 371 000 m
 * Reference point: the circle's centre; for a polygon the arithmetic mean of its distinct vertices.
 *
 * ADR 0003: a geofence alarm is a notification only; nothing here sends a command to a vehicle.
 */
import type { VehicleType } from "./domain.js";

export const GEOFENCE_KINDS = ["forbidden", "work_area", "dwell"] as const;
export type GeofenceKind = (typeof GEOFENCE_KINDS)[number];

export const GEOFENCE_SHAPES = ["polygon", "circle"] as const;
export type GeofenceShapeType = (typeof GEOFENCE_SHAPES)[number];

/** Alarm type raised by each kind (alarms.type). */
export const GEOFENCE_ALARM_TYPE = {
  forbidden: "geofence_enter",
  work_area: "geofence_exit",
  dwell: "geofence_dwell",
} as const satisfies Record<GeofenceKind, string>;

/** [lon, lat] like GeoJSON (geofences.coords). */
export type LonLat = [number, number];

export const GEOFENCE_LIMITS = {
  nameMax: 80,
  minVertices: 3,
  maxVertices: 200,
  minRadiusM: 5,
  maxRadiusM: 5000,
  /**
   * Minimum zone width (owner decision B, ADR 0015): GNSS wanders 2–5 m on site, so narrower zones
   * (walkways) cannot be watched reliably. A circle needs radius ≥ minWidthM / 2; a polygon needs an
   * inscribed circle of that radius (see polygonWideEnough). Otherwise the issue is `too_narrow`.
   */
  minWidthM: 10,
  minDwellS: 60,
  maxDwellS: 86_400,
  /** Active and inactive zones per site (ADR 0015 consequences). */
  perSite: 50,
} as const;

/** Evaluation constants (ADR 0015). */
export const GEOFENCE_RULES = {
  /** A point counts only if its source is GNSS and, when an accuracy is given, it is at most this (m). */
  maxAccuracyM: 15,
  /** Inside if ≥ this far inside the boundary, outside if ≥ this far outside; the band between changes nothing. */
  hysteresisM: 3,
  /** A new state must be observed without interruption for this long before it is accepted … */
  debounceMs: 5000,
  /**
   * … and by at least this many consecutive qualifying points (QA-6B-05; tech lead: 5). Moving, the 1-s
   * `pts` give >= 5 points in 5 s, so detection is unchanged; parked (30 s) it takes 5 positions in a row.
   */
  minObservations: 5,
  /**
   * The minute cron raises a dwell alarm only if the vehicle's newest evaluated GNSS point is at most this
   * old (owner decision A): a parked or indoor vehicle that stopped sending GNSS fixes does not alarm.
   */
  dwellFreshMs: 300_000,
} as const;
export type GeofenceRules = { maxAccuracyM: number; hysteresisM: number; debounceMs: number; minObservations: number; dwellFreshMs: number };

/** Mean Earth radius used by the projection (SQL uses the same constant). */
export const EARTH_RADIUS_M = 6_371_000;

/** Geometry only: exactly the shape columns of public.geofences. */
export type GeofenceGeometry = {
  shape: GeofenceShapeType;
  /** Polygon vertices [[lon, lat], …] without the closing vertex; null for circles. */
  coords: LonLat[] | null;
  centerLat: number | null;
  centerLon: number | null;
  radiusM: number | null;
};

/** One zone as the console sees it (public.geofences in camelCase). */
export type Geofence = GeofenceGeometry & {
  id: string;
  tenantId: string;
  siteId: string;
  name: string;
  kind: GeofenceKind;
  /** Empty = every vehicle type. */
  vehicleTypes: VehicleType[];
  /** Seconds; only for `dwell`, else null. */
  dwellLimitS: number | null;
  active: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
};

/* ---------------- projection and distances ---------------- */

export type XY = { x: number; y: number };
export type Projection = { lat0: number; lon0: number; project: (lat: number, lon: number) => XY };

const RAD = Math.PI / 180;

export function makeProjection(lat0: number, lon0: number): Projection {
  const kx = Math.cos(lat0 * RAD) * EARTH_RADIUS_M * RAD;
  const ky = EARTH_RADIUS_M * RAD;
  return { lat0, lon0, project: (lat, lon) => ({ x: (lon - lon0) * kx, y: (lat - lat0) * ky }) };
}

/** Polygon vertices with a repeated closing vertex removed. */
export function openRing(coords: readonly LonLat[]): LonLat[] {
  const out = coords.map((c) => [c[0], c[1]] as LonLat);
  while (out.length > 1) {
    const a = out[0]!;
    const b = out[out.length - 1]!;
    if (a[0] === b[0] && a[1] === b[1]) out.pop();
    else break;
  }
  return out;
}

/** Projection reference point [lat, lon] of a shape; null when the geometry is incomplete. */
export function referencePoint(g: GeofenceGeometry): { lat: number; lon: number } | null {
  if (g.shape === "circle") {
    return g.centerLat === null || g.centerLon === null ? null : { lat: g.centerLat, lon: g.centerLon };
  }
  const ring = openRing(g.coords ?? []);
  if (ring.length === 0) return null;
  let lat = 0;
  let lon = 0;
  for (const [x, y] of ring) {
    lon += x;
    lat += y;
  }
  return { lat: lat / ring.length, lon: lon / ring.length };
}

function segDistance(p: XY, a: XY, b: XY): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  let t = len2 === 0 ? 0 : ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const ex = p.x - (a.x + t * dx);
  const ey = p.y - (a.y + t * dy);
  // sqrt of the sum (not Math.hypot): the same floating-point operations as the SQL port.
  return Math.sqrt(ex * ex + ey * ey);
}

/** Even–odd ray casting on projected vertices. Points exactly on the boundary may go either way. */
export function pointInRing(p: XY, ring: readonly XY[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i]!;
    const b = ring[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

/** Is the point inside the polygon? (Planar, on the shape's projection.) */
export function pointInPolygon(lat: number, lon: number, coords: readonly LonLat[]): boolean {
  const ring = openRing(coords);
  const ref = referencePoint({ shape: "polygon", coords: ring, centerLat: null, centerLon: null, radiusM: null });
  if (!ref || ring.length < 3) return false;
  const pr = makeProjection(ref.lat, ref.lon);
  return pointInRing(
    pr.project(lat, lon),
    ring.map(([x, y]) => pr.project(y, x)),
  );
}

/**
 * Signed distance in metres from the point to the shape's boundary: positive inside, negative outside.
 * null when the geometry is incomplete (no centre/radius, fewer than 3 vertices).
 */
export function signedDistanceM(g: GeofenceGeometry, lat: number, lon: number): number | null {
  const ref = referencePoint(g);
  if (!ref) return null;
  const pr = makeProjection(ref.lat, ref.lon);
  const p = pr.project(lat, lon);
  if (g.shape === "circle") {
    if (g.radiusM === null) return null;
    return g.radiusM - Math.sqrt(p.x * p.x + p.y * p.y);
  }
  const ring = openRing(g.coords ?? []).map(([x, y]) => pr.project(y, x));
  return ringSignedDistance(p, ring);
}

/** Signed distance of a projected point to a projected ring (positive inside); null below 3 vertices. */
export function ringSignedDistance(p: XY, ring: readonly XY[]): number | null {
  if (ring.length < 3) return null;
  let d = Number.POSITIVE_INFINITY;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) d = Math.min(d, segDistance(p, ring[j]!, ring[i]!));
  return pointInRing(p, ring) ? d : -d;
}

/* ---------------- minimum width (owner decision B) ---------------- */

/** Tolerance of the width check (m): inscribed radii in [r − tol, r) may go either way. */
export const WIDTH_TOLERANCE_M = 0.05;
/** Work budget of the width check in edge-distance evaluations; beyond it the shape is accepted (fail open). */
export const WIDTH_BUDGET = 400_000;
const SQRT2 = 1.4142135623730951;

/**
 * Does the projected ring contain a circle of radius `r` (m)? Deterministic grid refinement (a
 * breadth-first variant of the "pole of inaccessibility" search), ported 1:1 to SQL
 * (fts_geofence_ring_wide_enough):
 *   1. Bounding box of the ring; cell size s = min(width, height); s < 2·(r − tol) → false. A grid of
 *      ceil(w/s) × ceil(h/s) cells, row by row (south to north, west to east), half = s/2; a grid whose
 *      cells × vertices exceed the budget → true.
 *   2. Level by level, in order: d = signed distance of the cell centre. d ≥ r − tol → true. A cell whose
 *      upper bound d + half·√2 < r − tol is dropped; a cell with half·√2 ≤ tol is not split further;
 *      otherwise its four children (SW, SE, NW, NE) go to the next level.
 *   3. No cells left → false. More than WIDTH_BUDGET edge evaluations → true (only odd, near-threshold
 *      shapes get there; the check is a usability guard, not a security boundary).
 * Guarantee: true when the largest inscribed circle has radius ≥ r; false when it is < r − tol.
 */
export function ringWideEnough(ring: readonly XY[], r: number, tol = WIDTH_TOLERANCE_M, budget = WIDTH_BUDGET): boolean {
  const n = ring.length;
  if (n < 3) return false;
  let minx = Infinity;
  let maxx = -Infinity;
  let miny = Infinity;
  let maxy = -Infinity;
  for (const p of ring) {
    if (p.x < minx) minx = p.x;
    if (p.x > maxx) maxx = p.x;
    if (p.y < miny) miny = p.y;
    if (p.y > maxy) maxy = p.y;
  }
  const w = maxx - minx;
  const h = maxy - miny;
  const s = Math.min(w, h);
  const goal = r - tol;
  // A bounding box narrower than 2·(r − tol) cannot hold the circle.
  if (!(s >= 2 * goal) || !(s > 0)) return false;
  const nx = Math.ceil(w / s);
  const ny = Math.ceil(h / s);
  if (nx * ny * n > budget) return true;
  let cells: number[] = []; // flat triples: cx, cy, half
  for (let j = 0; j < ny; j++) for (let i = 0; i < nx; i++) cells.push(minx + (i + 0.5) * s, miny + (j + 0.5) * s, s / 2);
  let work = 0;
  while (cells.length > 0) {
    const next: number[] = [];
    for (let k = 0; k < cells.length; k += 3) {
      const cx = cells[k]!;
      const cy = cells[k + 1]!;
      const half = cells[k + 2]!;
      const d = ringSignedDistance({ x: cx, y: cy }, ring)!;
      work += n;
      if (d >= goal) return true;
      if (d + half * SQRT2 < goal) continue;
      if (half * SQRT2 <= tol) continue;
      if (work > budget) return true;
      const q = half / 2;
      next.push(cx - q, cy - q, q, cx + q, cy - q, q, cx - q, cy + q, q, cx + q, cy + q, q);
    }
    cells = next;
  }
  return false;
}

/** Polygon form of the minimum-width rule: an inscribed circle of radius minWidthM / 2 fits. */
export function polygonWideEnough(coords: readonly LonLat[], minWidthM: number = GEOFENCE_LIMITS.minWidthM): boolean {
  const ring = openRing(coords);
  const ref = referencePoint({ shape: "polygon", coords: ring, centerLat: null, centerLon: null, radiusM: null });
  if (!ref || ring.length < 3) return false;
  const pr = makeProjection(ref.lat, ref.lon);
  return ringWideEnough(
    ring.map(([x, y]) => pr.project(y, x)),
    minWidthM / 2,
  );
}

/* ---------------- names ---------------- */

/**
 * Whitespace trimmed from zone names: exactly ECMAScript's WhiteSpace + LineTerminator set (what
 * String.prototype.trim removes), written out so the SQL guard uses the identical class (QA-6B-04).
 */
const NAME_TRIM_RE = /^[\u0009-\u000D\u0020\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000\uFEFF]+|[\u0009-\u000D\u0020\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000\uFEFF]+$/gu;

/** The stored form of a zone name (trimmed exactly like the SQL guard). */
export function normalizeGeofenceName(name: string): string {
  return (name ?? "").replace(NAME_TRIM_RE, "");
}

/** Name length in Unicode code points (PostgreSQL char_length), not UTF-16 units. */
export const geofenceNameLength = (name: string): number => Array.from(name).length;

/* ---------------- validation ---------------- */

export const GEOFENCE_ISSUES = [
  "name_required",
  "name_too_long",
  "invalid_kind",
  "invalid_shape",
  "invalid_coordinate",
  "too_few_vertices",
  "too_many_vertices",
  "self_intersecting",
  "too_narrow",
  "radius_out_of_range",
  "dwell_out_of_range",
  "dwell_only_for_dwell",
  "invalid_vehicle_type",
] as const;
export type GeofenceIssue = (typeof GEOFENCE_ISSUES)[number];

export type GeofenceDraft = GeofenceGeometry & {
  name: string;
  kind: string;
  dwellLimitS: number | null;
  vehicleTypes?: readonly string[];
};

const finite = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const validLat = (v: unknown) => finite(v) && v >= -90 && v <= 90;
const validLon = (v: unknown) => finite(v) && v >= -180 && v <= 180;

function orient(a: XY, b: XY, c: XY): number {
  const v = (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
  // Treat sub-millimetre-square areas as collinear (projected metres).
  return Math.abs(v) < 1e-6 ? 0 : v > 0 ? 1 : -1;
}

function onSegment(a: XY, b: XY, p: XY): boolean {
  return Math.min(a.x, b.x) - 1e-9 <= p.x && p.x <= Math.max(a.x, b.x) + 1e-9 && Math.min(a.y, b.y) - 1e-9 <= p.y && p.y <= Math.max(a.y, b.y) + 1e-9;
}

/** Do segments ab and cd touch or cross (including collinear overlap)? */
export function segmentsIntersect(a: XY, b: XY, c: XY, d: XY): boolean {
  const o1 = orient(a, b, c);
  const o2 = orient(a, b, d);
  const o3 = orient(c, d, a);
  const o4 = orient(c, d, b);
  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && onSegment(a, b, c)) return true;
  if (o2 === 0 && onSegment(a, b, d)) return true;
  if (o3 === 0 && onSegment(c, d, a)) return true;
  if (o4 === 0 && onSegment(c, d, b)) return true;
  return false;
}

/**
 * A simple polygon: no two non-adjacent edges touch, adjacent edges meet only at their shared vertex
 * and no edge has zero length. (Planar check on the shape's projection.)
 */
export function isSimplePolygon(coords: readonly LonLat[]): boolean {
  const ring = openRing(coords);
  const n = ring.length;
  if (n < 3) return false;
  const ref = referencePoint({ shape: "polygon", coords: ring, centerLat: null, centerLon: null, radiusM: null })!;
  const pr = makeProjection(ref.lat, ref.lon);
  const pts = ring.map(([x, y]) => pr.project(y, x));
  for (let i = 0; i < n; i++) {
    const a = pts[i]!;
    const b = pts[(i + 1) % n]!;
    if (Math.hypot(b.x - a.x, b.y - a.y) < 1e-3) return false;
  }
  for (let i = 0; i < n; i++) {
    const a = pts[i]!;
    const b = pts[(i + 1) % n]!;
    for (let j = i + 1; j < n; j++) {
      const c = pts[j]!;
      const d = pts[(j + 1) % n]!;
      const adjacent = j === i + 1 || (i === 0 && j === n - 1);
      if (!adjacent) {
        if (segmentsIntersect(a, b, c, d)) return false;
        continue;
      }
      // Adjacent edges share one vertex; they must not fold back onto each other.
      const [p, shared, q] = j === i + 1 ? [a, b, d] : [b, a, c];
      if (orient(p, shared, q) === 0) {
        // Collinear: fine only if q continues away from p (a straight-through vertex).
        const dot = (shared.x - p.x) * (q.x - shared.x) + (shared.y - p.y) * (q.y - shared.y);
        if (dot <= 0) return false;
      }
    }
  }
  return true;
}

/** Every rule a zone must satisfy before it is saved (same as the table's checks). Empty = valid. */
export function validateGeofence(d: GeofenceDraft, knownVehicleTypes?: readonly string[]): GeofenceIssue[] {
  const issues: GeofenceIssue[] = [];
  const len = geofenceNameLength(normalizeGeofenceName(d.name ?? ""));
  if (len === 0) issues.push("name_required");
  else if (len > GEOFENCE_LIMITS.nameMax) issues.push("name_too_long");
  if (!(GEOFENCE_KINDS as readonly string[]).includes(d.kind)) issues.push("invalid_kind");

  if (d.shape === "polygon") {
    const ring = openRing(d.coords ?? []);
    if (!ring.every((c) => Array.isArray(c) && validLon(c[0]) && validLat(c[1]))) issues.push("invalid_coordinate");
    else if (ring.length < GEOFENCE_LIMITS.minVertices) issues.push("too_few_vertices");
    else if (ring.length > GEOFENCE_LIMITS.maxVertices) issues.push("too_many_vertices");
    else if (!isSimplePolygon(ring)) issues.push("self_intersecting");
    else if (!polygonWideEnough(ring)) issues.push("too_narrow");
  } else if (d.shape === "circle") {
    if (!validLat(d.centerLat) || !validLon(d.centerLon)) issues.push("invalid_coordinate");
    // Below 5 m the circle is narrower than the 10 m minimum width (minRadiusM = minWidthM / 2).
    if (!finite(d.radiusM) || d.radiusM > GEOFENCE_LIMITS.maxRadiusM) issues.push("radius_out_of_range");
    else if (d.radiusM < GEOFENCE_LIMITS.minRadiusM) issues.push("too_narrow");
  } else {
    issues.push("invalid_shape");
  }

  if (d.kind === "dwell") {
    if (!finite(d.dwellLimitS) || !Number.isInteger(d.dwellLimitS) || d.dwellLimitS < GEOFENCE_LIMITS.minDwellS || d.dwellLimitS > GEOFENCE_LIMITS.maxDwellS) {
      issues.push("dwell_out_of_range");
    }
  } else if (d.dwellLimitS !== null && d.dwellLimitS !== undefined) {
    issues.push("dwell_only_for_dwell");
  }
  if (knownVehicleTypes && (d.vehicleTypes ?? []).some((t) => !knownVehicleTypes.includes(t))) issues.push("invalid_vehicle_type");
  return issues;
}

/** Does the zone apply to a vehicle of this type? (Inactive zones never apply; empty list = all types.) */
export function geofenceApplies(g: Pick<Geofence, "active" | "vehicleTypes">, vehicleType: VehicleType | string | null): boolean {
  if (!g.active) return false;
  if (g.vehicleTypes.length === 0) return true;
  return vehicleType !== null && (g.vehicleTypes as readonly string[]).includes(vehicleType);
}

/* ---------------- state machine ---------------- */

/**
 * Per vehicle and zone (public.geofence_state). Times are ISO instants (device time).
 * A vehicle without a row starts from `initialGeofenceState()` (outside).
 */
export type GeofenceState = {
  /** Accepted state. */
  inside: boolean;
  /** When the accepted state began (first point of the confirmed candidate); null = never confirmed. */
  since: string | null;
  /** A pending state change, or null. */
  candidate: boolean | null;
  candidateSince: string | null;
  /** Consecutive qualifying points of the pending candidate (0 without one). */
  candidateCount: number;
  /** Newest point that was evaluated; older or equal points change nothing. */
  lastPointAt: string | null;
  /** The dwell alarm of the current stay was raised (once per entry). */
  dwellAlerted: boolean;
};

export const initialGeofenceState = (): GeofenceState => ({
  inside: false,
  since: null,
  candidate: null,
  candidateSince: null,
  candidateCount: 0,
  lastPointAt: null,
  dwellAlerted: false,
});

/** One position: `t` = ts − dt_ms (ISO); `src` = pos.src; `acc` metres or null when the device sent none. */
export type GeofencePoint = { t: string; lat: number; lon: number; src: string | null; acc: number | null };

/** The zone fields the evaluation needs. */
export type GeofenceRuleShape = GeofenceGeometry & Pick<Geofence, "kind" | "dwellLimitS">;

export type GeofenceEventType = "enter" | "exit" | "dwell";

/** An alarm-worthy event: `at`/`lat`/`lon` are the violation point (null position for the dwell cron). */
export type GeofenceEvent = { type: GeofenceEventType; at: string; lat: number | null; lon: number | null };

/** Why a point did not take part in the evaluation. */
export type GeofenceSkip = "not_gnss" | "low_accuracy" | "stale" | "invalid_point" | "invalid_shape";

export type GeofenceStep = { state: GeofenceState; events: GeofenceEvent[]; skipped: GeofenceSkip | null };

const toMs = (iso: string | null): number | null => {
  if (iso === null) return null;
  const v = Date.parse(iso);
  return Number.isFinite(v) ? v : null;
};
const toIso = (ms: number) => new Date(ms).toISOString();

/** The event a confirmed transition raises for a kind (forbidden: enter; work area: exit; dwell: none). */
function transitionEvent(kind: GeofenceKind, nowInside: boolean): GeofenceEventType | null {
  if (kind === "forbidden" && nowInside) return "enter";
  if (kind === "work_area" && !nowInside) return "exit";
  return null;
}

function dwellDue(state: GeofenceState, shape: Pick<GeofenceRuleShape, "kind" | "dwellLimitS">, nowMs: number): boolean {
  if (shape.kind !== "dwell" || !state.inside || state.dwellAlerted || shape.dwellLimitS === null) return false;
  const since = toMs(state.since);
  return since !== null && nowMs - since >= shape.dwellLimitS * 1000;
}

/**
 * Evaluate one point (ADR 0015). Rules, in order:
 * 1. Only GNSS points (`src = "gnss"`) with accuracy ≤ 15 m (or none given); others are skipped.
 * 2. Points at or before `lastPointAt` (late buffered data, duplicates) change nothing.
 * 3. Signed distance ≥ +3 m = observed inside, ≤ −3 m = observed outside, otherwise (the band) no observation.
 * 4. An observation different from the accepted state starts (or continues) a candidate; once the candidate
 *    has lasted ≥ 5 s (t − candidateSince) AND has been observed by ≥ 5 consecutive points it is accepted,
 *    `since` = candidateSince. A band point or an observation equal to the accepted state clears the
 *    candidate ("without interruption").
 * 5. Accepted transitions raise `enter` (forbidden) or `exit` (work_area) at the confirming point.
 * 6. Dwell zones raise `dwell` once per stay, at the first evaluated point with t − since ≥ dwellLimitS.
 */
export function stepGeofence(state: GeofenceState, point: GeofencePoint, shape: GeofenceRuleShape, rules: GeofenceRules = GEOFENCE_RULES): GeofenceStep {
  const skip = (why: GeofenceSkip): GeofenceStep => ({ state, events: [], skipped: why });
  const t = toMs(point.t);
  if (t === null || !validLat(point.lat) || !validLon(point.lon)) return skip("invalid_point");
  if (point.src !== "gnss") return skip("not_gnss");
  if (point.acc !== null && point.acc !== undefined && !(point.acc <= rules.maxAccuracyM)) return skip("low_accuracy");
  const last = toMs(state.lastPointAt);
  if (last !== null && t <= last) return skip("stale");
  const d = signedDistanceM(shape, point.lat, point.lon);
  if (d === null) return skip("invalid_shape");

  const at = toIso(t);
  const next: GeofenceState = { ...state, lastPointAt: at };
  const events: GeofenceEvent[] = [];
  const observed = d >= rules.hysteresisM ? true : d <= -rules.hysteresisM ? false : null;

  if (observed === null || observed === next.inside) {
    next.candidate = null;
    next.candidateSince = null;
    next.candidateCount = 0;
  } else {
    if (next.candidate !== observed) {
      next.candidate = observed;
      next.candidateSince = at;
      next.candidateCount = 1;
    } else {
      next.candidateCount = state.candidateCount + 1;
    }
    const since = toMs(next.candidateSince)!;
    if (t - since >= rules.debounceMs && next.candidateCount >= rules.minObservations) {
      next.inside = observed;
      next.since = next.candidateSince;
      next.candidate = null;
      next.candidateSince = null;
      next.candidateCount = 0;
      next.dwellAlerted = false;
      const ev = transitionEvent(shape.kind, observed);
      if (ev) events.push({ type: ev, at, lat: point.lat, lon: point.lon });
    }
  }

  if (dwellDue(next, shape, t)) {
    next.dwellAlerted = true;
    events.push({ type: "dwell", at, lat: point.lat, lon: point.lon });
  }
  return { state: next, events, skipped: null };
}

/**
 * The minute cron (ADR 0015): a vehicle that is still inside a dwell zone past its limit raises `dwell`
 * even when no new telemetry arrives — but only while its newest evaluated GNSS point is at most
 * 5 minutes old (owner decision A: a parked or indoor vehicle does not alarm). Once per stay; the event
 * has no position.
 */
export function checkDwell(
  state: GeofenceState,
  now: string | Date,
  shape: Pick<GeofenceRuleShape, "kind" | "dwellLimitS">,
  rules: GeofenceRules = GEOFENCE_RULES,
): GeofenceStep {
  const t = typeof now === "string" ? toMs(now) : now.getTime();
  const last = toMs(state.lastPointAt);
  if (t === null || !Number.isFinite(t) || last === null || last < t - rules.dwellFreshMs || !dwellDue(state, shape, t)) {
    return { state, events: [], skipped: null };
  }
  return { state: { ...state, dwellAlerted: true }, events: [{ type: "dwell", at: toIso(t), lat: null, lon: null }], skipped: null };
}

/** Run a sequence of points (oldest first, as ingest does). */
export function runGeofence(points: readonly GeofencePoint[], shape: GeofenceRuleShape, start: GeofenceState = initialGeofenceState(), rules: GeofenceRules = GEOFENCE_RULES): { state: GeofenceState; events: GeofenceEvent[] } {
  let state = start;
  const events: GeofenceEvent[] = [];
  for (const p of points) {
    const r = stepGeofence(state, p, shape, rules);
    state = r.state;
    events.push(...r.events);
  }
  return { state, events };
}

/* ---------------- helpers for drawing ---------------- */

/** A circle as a polygon ring [[lon, lat], …] for display (not for evaluation). */
export function circleRing(centerLat: number, centerLon: number, radiusM: number, segments = 48): LonLat[] {
  const kLat = 1 / (EARTH_RADIUS_M * RAD);
  const kLon = kLat / Math.cos(centerLat * RAD);
  const out: LonLat[] = [];
  for (let i = 0; i < segments; i++) {
    const a = (2 * Math.PI * i) / segments;
    out.push([centerLon + Math.cos(a) * radiusM * kLon, centerLat + Math.sin(a) * radiusM * kLat]);
  }
  return out;
}

/** Distance in metres between two points on the projection around the first one. */
export function geofenceDistanceM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const p = makeProjection(lat1, lon1).project(lat2, lon2);
  return Math.hypot(p.x, p.y);
}
